import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import ErrorHandler from "@/core/services/ErrorHandler";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);

    ApiService.vueInstance.axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}ppdb/v2/`
    ApiService.setHeader();
    // "https://api-ppdb.tkilocal.biz.id/ppdb/v2/";
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    const token = JwtService.getToken();

    if (token) {
      ApiService.vueInstance.axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      return;
    }

    delete ApiService.vueInstance.axios.defaults.headers.common.Authorization;
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params).catch((error) => {
      throw new Error(`[KT] ApiService ${error}`);
    });
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    slug = "" as string
  ): Promise<AxiosResponse> {
    return axios.get(`${process.env.VUE_APP_API_LOGIN_URL}ppdb/v2/${resource}`);
  }

  public static getWithData(resource: string, data: any): Promise<any> {
    return ApiService.vueInstance.axios.get(`${resource}`, data);
  }

  public static getResetPassword(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(`${resource}`);
  }

  public static getWithoutSlug(resource: string): Promise<AxiosResponse> {
    // console.log(resource)
    if (!resource.includes("credential")) {
      if (
        JSON.parse(localStorage.getItem("user_account")!).role != "SUPER_ADMIN"
      ) {
        //ubah endpoint office menjadi admin
        let endpoint = resource.replace("office", "admin");
        resource = endpoint;
      }
    }
    return ApiService.vueInstance.axios.get(`${resource}`);
  }

  public static getEndPointCRM(resource: string): Promise<AxiosResponse> {
    const instance = axios.create();
    return instance.get(
      `${process.env.VUE_APP_API_UPLOAD_URL}crmv2/${resource}`
    );
  }

  public static postEndPointCRM(
    resource: string,
    data: any
  ): Promise<AxiosResponse> {
    const instance = axios.create();
    return instance.post(
      `${process.env.VUE_APP_API_UPLOAD_URL}crmv2/${resource}`,
      data
    );
  }

  public static getDownloadCRM(resource: string): Promise<AxiosResponse> {
    const instance = axios.create({ responseType: "blob" });
    return instance.get(
      `${process.env.VUE_APP_API_UPLOAD_URL}crmv2/${resource}`
    );

    // const instance = ApiService.vueInstance.axios.create({ responseType: "blob" });
    // return instance.get(`${resource}`)
  }

  public static getDownload(resource: string): Promise<AxiosResponse> {
    const instance = ApiService.vueInstance.axios.create({
      responseType: "blob",
    });
    return instance.get(`${resource}`);
  }

  public static postWithoutSlug(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`);
  }

  public static postWithData(resource: string, data: any): Promise<any> {
    if (!resource.includes("credential")) {
      if (
        JSON.parse(localStorage.getItem("user_account")!).role != "SUPER_ADMIN"
      ) {
        //ubah endpoint office menjadi admin
        let endpoint = resource.replace("office", "admin");
        resource = endpoint;
      }
    }

    return ApiService.vueInstance.axios.post(`${resource}`, data);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params);
  }

  /**
   * @description set the POST LOGIN HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static postlogin(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<any> {
    // return console.log(
    //   `${process.env.VUE_APP_API_LOGIN_URL}ppdb/v2/${resource}`
    // );
    return axios.post(
      `${process.env.VUE_APP_API_LOGIN_URL}ppdb/v2/${resource}`,
      params
    );
    //   "https://api.dev.teknologikartu.com/ppdb/v2/" + resource,
    //   params
    // );
  }
  public static PostMethodWithoutData(resource: string): Promise<any> {
    return ApiService.vueInstance.axios.post(`${resource}`);
  }

  public static PutMethodWithoutData(resource: string): Promise<any> {
    if (!resource.includes("credential")) {
      if (
        JSON.parse(localStorage.getItem("user_account")!).role != "SUPER_ADMIN"
      ) {
        //ubah endpoint office menjadi admin
        let endpoint = resource.replace("office", "admin");
        resource = endpoint;
      }
    }

    return ApiService.vueInstance.axios.put(`${resource}`);
  }

  public static PostMethod(resource: string, data: any): Promise<any> {
    return ApiService.vueInstance.axios.post(`${resource}`, data);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  public static putWithData(resource: string, data: any): Promise<any> {
    if (!resource.includes("credential")) {
      if (
        JSON.parse(localStorage.getItem("user_account")!).role != "SUPER_ADMIN"
      ) {
        //ubah endpoint office menjadi admin
        let endpoint = resource.replace("office", "admin");
        resource = endpoint;
      }
    }

    return ApiService.vueInstance.axios.put(`${resource}`, data);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    if (!resource.includes("credential")) {
      if (
        JSON.parse(localStorage.getItem("user_account")!).role != "SUPER_ADMIN"
      ) {
        //ubah endpoint office menjadi admin
        let endpoint = resource.replace("office", "admin");
        resource = endpoint;
      }
    }

    return ApiService.vueInstance.axios.delete(resource);

    // return ApiService.vueInstance.axios.delete(resource).catch((error) => {
    //   throw new Error(`[RWV] ApiService ${error}`);
    // });
  }
  public static getDownloadExternalPpdb(
    resource: string
  ): Promise<AxiosResponse> {
    const instance = ApiService.vueInstance.axios.create({
      responseType: "blob",
    });
    return instance.get(`${process.env.VUE_APP_API_LOGIN_URL}${resource}`);
  }

  public static getExternalCrm(resource: string): Promise<AxiosResponse> {
    const instance = ApiService.vueInstance.axios.create({});
    return instance.get(`${process.env.VUE_APP_API_UPLOAD_URL}${resource}`);
  }
  public static getExternalPpdb(resource: string): Promise<AxiosResponse> {
    const instance = ApiService.vueInstance.axios.create({});
    return instance.get(`${process.env.VUE_APP_API_LOGIN_URL}${resource}`);
  }

  public static getDownloadExternalCrm(
    resource: string
  ): Promise<AxiosResponse> {
    const instance = ApiService.vueInstance.axios.create({
      responseType: "blob",
    });
    return instance.get(`${process.env.VUE_APP_API_UPLOAD_URL}${resource}`);
  }

  public static postExternalAuthPpdb(
    resource: string,
    data: any = {}
  ): Promise<any> {
    return axios.post(`${process.env.VUE_APP_API_LOGIN_URL}${resource}`, data);
  }

  public static postExternalAuthCrm(
    resource: string,
    data: any = {}
  ): Promise<any> {
    console.log(resource);

    return axios.post(`${process.env.VUE_APP_API_UPLOAD_URL}${resource}`, data);
  }
}

export default ApiService;
